import type { AdminJobPage } from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "./admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "./admin-dashboard-copy";

type AdminJobRow = AdminJobPage["items"][number];

export type AdminJobRowSnapshotItemViewModel = {
  id: "oldestQueuedAge" | "retryPressure" | "failedProvider";
  label: string;
  value: string;
};

export type AdminJobRowSnapshotViewModel = {
  title: string;
  sourceGapLabel: string;
  items: AdminJobRowSnapshotItemViewModel[];
};

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return locale === "ko" ? ko : en;
}

function buildOldestQueuedAgeItem(
  rows: AdminJobRow[],
  generatedAt: string,
  locale: AdminDashboardLocale,
): AdminJobRowSnapshotItemViewModel | undefined {
  const activeQueuedTimes = rows
    .filter((row) => (row.status === "queued" || row.status === "processing") && row.queuedAt)
    .map((row) => Date.parse(row.queuedAt as string))
    .filter((value) => Number.isFinite(value));

  if (activeQueuedTimes.length === 0) {
    return undefined;
  }

  const generatedAtMs = Date.parse(generatedAt);
  if (!Number.isFinite(generatedAtMs)) {
    return undefined;
  }

  const oldestQueuedAtMs = Math.min(...activeQueuedTimes);
  const ageMinutes = Math.max(0, Math.floor((generatedAtMs - oldestQueuedAtMs) / 60000));

  let value = text(locale, "Under 15 min", "15분 미만");
  if (ageMinutes >= 60) {
    value = text(locale, "60+ min", "60분 이상");
  } else if (ageMinutes >= 30) {
    value = text(locale, "30-60 min", "30-60분");
  } else if (ageMinutes >= 15) {
    value = text(locale, "15-30 min", "15-30분");
  }

  return {
    id: "oldestQueuedAge",
    label: text(locale, "Oldest queued age", "최장 대기 시간"),
    value,
  };
}

function buildRetryPressureItem(
  rows: AdminJobRow[],
  locale: AdminDashboardLocale,
): AdminJobRowSnapshotItemViewModel | undefined {
  const retriedRows = rows.filter((row) => row.attemptCount >= 2);
  if (retriedRows.length === 0) {
    return undefined;
  }

  const elevated = retriedRows.some((row) => row.attemptCount >= 3) || retriedRows.length >= 2;

  return {
    id: "retryPressure",
    label: text(locale, "Retry pressure", "재시도 압력"),
    value: elevated ? text(locale, "Elevated", "상승") : text(locale, "Low", "낮음"),
  };
}

function buildFailedProviderItem(
  rows: AdminJobRow[],
  locale: AdminDashboardLocale,
): AdminJobRowSnapshotItemViewModel | undefined {
  const failedProviderRows = rows.filter(
    (row) => row.status === "failed" && typeof row.provider === "string" && row.provider.trim().length > 0,
  );

  if (failedProviderRows.length === 0) {
    return undefined;
  }

  const providerCounts = new Map<string, number>();
  for (const row of failedProviderRows) {
    providerCounts.set(row.provider!, (providerCounts.get(row.provider!) ?? 0) + 1);
  }

  const topProvider = [...providerCounts.entries()].sort((left, right) => {
    if (right[1] !== left[1]) {
      return right[1] - left[1];
    }
    return left[0].localeCompare(right[0]);
  })[0]?.[0];

  if (!topProvider) {
    return undefined;
  }

  return {
    id: "failedProvider",
    label: text(locale, "Failed provider", "실패 제공자"),
    value: text(locale, `${topProvider} dominant`, `${topProvider} 집중`),
  };
}

export function buildAdminJobRowSnapshotViewModel(
  rows: AdminJobRow[],
  generatedAt: string,
  locale: AdminDashboardLocale,
): AdminJobRowSnapshotViewModel | undefined {
  const items = [
    buildOldestQueuedAgeItem(rows, generatedAt, locale),
    buildRetryPressureItem(rows, locale),
    buildFailedProviderItem(rows, locale),
  ].flatMap((item) => (item ? [item] : []));

  if (items.length === 0) {
    return undefined;
  }

  return {
    title: pickAdminDashboardLocaleText(locale, {
      en: "Current row snapshot",
      ko: "현재 행 스냅샷",
    }),
    sourceGapLabel: pickAdminDashboardLocaleText(locale, {
      en: "Derived from the visible job rows only. Use it to choose the next filter or drilldown, not as proof that upstream observability aggregates are populated.",
      ko: "이 화면에 이미 로드된 visible job rows만으로 다시 계산한 snapshot입니다. 다음 필터나 drilldown을 고르는 데 사용하고, upstream 관측 집계 source가 채워졌다는 증거로 읽지 마세요.",
    }),
    items,
  };
}
